import { Component, inject, signal } from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { ILinkAccountState, TAuthProvider } from '../../core/auth/auth.interface';
import { AuthStore } from '../../core/auth/auth.store';
import { AuthzService } from '../../core/auth/authz.service';
import { AuthService } from '@auth0/auth0-angular';
import { AuthFacade } from '../../core/auth/auth.facade';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTER_TOKENS } from '../../app.routes';
// RxJS
import { catchError, merge, take, tap, throwError } from 'rxjs';
// Service
import { CompanyService } from '../../core/company/company.service';
import { LoggingService } from '../../core/utils/logging.service';
// Directive
import { NoneSpecialCharDirective } from '../../core/directive/none-special-char.directive';
import { TrimDirective } from '../../core/directive/trim.directive';
// PrimeNG
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { ButtonModule } from 'primeng/button';
// Utility
import { formatChar } from '../../share/utility/format-char';
// Component
import { UploadImageComponent } from '../../pattern/upload-image/upload-image.component';
import {
  DialogAlertComponent,
  IDialogData,
  PATH_ICON_ALERT_DIALOG,
} from '../../ui/dialog-alert/dialog-alert.component';
import { LanguageConfigComponent } from '../../pattern/language-config/language-config.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import CountryList from 'country-list-with-dial-code-and-flag';
import {
  CountryPhoneSelectComponent,
  IPhoneChangeResult,
} from '../../pattern/country-phone-select/country-phone-select.component';
import { ForbiddenWordValidatorService } from '../../core/utils/forbidden-word-validator.service';

export interface IFormUserProfile {
  firstName: string;
  lastName: string;
  nickname: string;
  avatarUrl: string;
  email: string;
  mobile: string;
  name: string;
  ownerCountry: string;
}

const PRIMENG = [ImageModule, InputTextModule, InputMaskModule, ButtonModule];

@Component({
  selector: 'app-login-profile',
  imports: [
    FormsModule,
    NoneSpecialCharDirective,
    TrimDirective,
    ...PRIMENG,
    UploadImageComponent,
    DialogAlertComponent,
    LanguageConfigComponent,
    TranslocoModule,
    CountryPhoneSelectComponent,
  ],
  templateUrl: './login-profile.component.html',
  styleUrl: './login-profile.component.scss',
})
export class LoginProfileComponent {
  // Injectable Service
  readonly router = inject(Router);
  authStore = inject(AuthStore);
  companyService = inject(CompanyService);
  auth0 = inject(AuthService);
  activatedRoute = inject(ActivatedRoute);
  authzService = inject(AuthzService);
  authFacade = inject(AuthFacade);
  private readonly loggingService = inject(LoggingService);
  private readonly translocoService = inject(TranslocoService);
  private readonly forbiddenWordValidatorService = inject(ForbiddenWordValidatorService);

  loadingButtonSignal = signal<boolean>(false);
  imageLineSignal = signal<string>('../../../assets/icons/icon-line.png');
  imageLineGreenSignal = signal<string>('../../../assets/icons/icon-line-green.png');
  hasSyncWithLine = signal<boolean>(false);
  hasSyncWithGoogle = signal<boolean>(false);
  isDuplicateNameSignal = signal<boolean>(false);
  isValidPhoneSignal = signal<boolean>(false);
  token: string | null = null;
  user: any | null = null;

  dialogAlertSignal = signal<IDialogData>({
    image: '',
    title: '',
    description: '',
    delay: 1000,
    visible: false,
  });

  formUserProfile = signal<IFormUserProfile>({
    firstName: this.authStore.user!.firstName ? this.authStore.user!.firstName : '',
    lastName: this.authStore.user!.lastName ? this.authStore.user!.lastName : '',
    nickname: this.authStore.user!.nickname ? this.authStore.user!.nickname : '',
    avatarUrl: this.authStore.user!.avatarUrl ? this.authStore.user!.avatarUrl : '',
    email: this.authStore.user!.email ? this.authStore.user!.email : '',
    mobile: this.authStore.user!.mobile ? this.authStore.user!.mobile : '',
    name: '',
    ownerCountry: '',
  });

  constructor() {
    this.loggingService.error('@@@UserProfileComponent@@@');
    this.loggingService.log('ROUTE>>>>>>>>>', window.location.href);
    this.auth0.user$.subscribe((user) => {
      this.loggingService.log('@@@user', user);
      if (user && user['user_metadata'] && user['user_metadata']['linkAccount']) {
        const userMetadata = user['user_metadata'];
        this.loggingService.log('userMetadata', userMetadata);
        const linkAccount = userMetadata['linkAccount'];
        if (linkAccount.includes('line')) {
          this.hasSyncWithLine.set(true);
        }
        if (linkAccount.includes('google')) {
          this.hasSyncWithGoogle.set(true);
        }
      }
    });

    this.loggingService.log('authStore >>>>>>>>>', this.authStore);

    const getAllCountryCode = CountryList.getAll();
    this.loggingService.log('ALL COUNTRY CODE :: List :: =>', getAllCountryCode);
  }

  onUploadImgUser(event: string, form: FormGroup) {
    this.formUserProfile.update((profile) => ({
      ...profile,
      avatarUrl: event,
    }));
    form.markAsDirty();
    this.loggingService.log('Func | onUploadImgUser', this.formUserProfile());
  }

  onDeleteImgUser(event: string, form: FormGroup) {
    this.formUserProfile.update((profile) => ({ ...profile, avatarUrl: '' }));
    form.markAsDirty();
    this.loggingService.log('Func | onDeleteImgUser', this.formUserProfile(), event);
  }

  onMobileChange(data: IPhoneChangeResult) {
    this.loggingService.log('Func | onMobileChange', data);

    this.isValidPhoneSignal.set(data.valid);

    if (data.valid) {
      this.formUserProfile.update((profile) => ({
        ...profile,
        mobile: data.data?.mobile || '',
        ownerCountry: data.data?.countryCodeISO2 || '',
      }));
    }
  }

  handleContinue() {
    this.loggingService.warn('Func | handleContinue', this.formUserProfile());
    const { firstName, lastName, nickname } = this.formUserProfile();
    const bodySend = {
      ...this.formUserProfile(),
      firstName: formatChar(firstName),
      lastName: formatChar(lastName),
      nickname: formatChar(nickname),
      name: this.formUserProfile().name.trim(),
      ownerCountry: this.formUserProfile().ownerCountry ?? 'TH',
      mobile: this.formUserProfile().mobile.trim(),
    };
    this.loggingService.log('bodySend', bodySend);
    this.loadingButtonSignal.set(true);
    this.companyService
      .createBusinessWithOwner(bodySend)
      .pipe(
        tap(() => {
          this.authFacade.updatePageRenderState({
            isFirstLoadOnSuccessNewUser: true,
          });
        }),
        catchError((error) => {
          let description = this.translateWord('try_again');

          this.loggingService.log('error', error.error.statusCode);
          if (this.isReservedBrandError(error)) {
            description = this.translateWord('reserved_brand_name');
          }

          if (error.error.statusCode === 409) {
            description = this.translateWord('login_profile.business_name_already_exists');
            this.isDuplicateNameSignal.set(true);
          }
          const dataDialog: IDialogData = {
            image: PATH_ICON_ALERT_DIALOG.FAIL,
            title: this.translateWord('login_profile.create_business_alert_fail'),
            description: description,
            delay: 0,
            visible: true,
            isClosable: true,
          };
          this.openDialogAlert(dataDialog);
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.router.navigate([`/${ROUTER_TOKENS.AUTH_SUCCESS_NEW_USER}`]);
      });
  }

  reAuthUser(provider: TAuthProvider) {
    this.loggingService.error('reAuthUser', this.activatedRoute.parent);
    this.loggingService.error('origin', window.location.origin);
    this.activatedRoute.url.subscribe(this.loggingService.warn);
    const currentRoute = this.activatedRoute.snapshot.url;
    this.loggingService.log(currentRoute);
    const callbackUrl = window.location.origin + '/' + currentRoute;
    this.loggingService.error('callbackUrl => ', callbackUrl);

    this.auth0
      .loginWithRedirect({
        authorizationParams: {
          prompt: 'login',
          redirect_uri: `${window.location.origin}/link-account`,
          scope: 'openid profile email', // update:users update:current_user_identities
        },
        appState: {
          target: `/${ROUTER_TOKENS.AUTH_LINK_ACCOUNT}`,
          callToRedirectRoute: window.location.href,
          socialProvider: provider,
        },
      })
      .subscribe(() => this.loggingService.log('logged in with secondary account'));
  }

  linkAccountHandler(provider: TAuthProvider) {
    merge(
      this.auth0.getAccessTokenSilently().pipe(
        tap((token) => (this.token = token ?? null)),
        take(1)
      ),
      this.auth0.user$.pipe(
        tap((user) => (this.user = user)),
        take(1)
      )
    ).subscribe({
      next: () => {
        this.loggingService.error('token', this.token);
        this.loggingService.error('user', this.user);
      },
      error: (error) => {
        this.loggingService.error('error', error);
      },
      complete: () => {
        this.loggingService.error('complete -> RE-AUTH');
        const oldAccount: ILinkAccountState = {
          accessToken: this.token!,
          sub: this.user!,
        };
        this.authzService.setLinkAccountState(oldAccount);
        this.reAuthUser(provider);
      },
    });
  }

  unlinkAccountHandler(provider: TAuthProvider) {
    this.loggingService.error('unlinkAccount for ', provider);
    this.authzService
      .unlinkAccount(provider)
      .pipe(
        tap(() => this.loggingService.warn('0️⃣###force fetching user token after unlink account')),
        tap(() => window.location.reload())
      )
      .subscribe();
  }

  disableBtn(form: FormGroup): boolean {
    // this.loggingService.log('disableBtn => ', form);
    // Create
    return form.valid;
  }

  validateBusinessNameInput(event: any): void {
    const input = event.target;
    const value = input.value;

    // Allow only English characters, numbers, parentheses, hyphens, @ symbol, and spaces
    // But prevent consecutive spaces and trim leading/trailing whitespace
    let filteredValue = value.replace(/[^a-zA-Z0-9()@_\-\s]/g, '');

    // Replace consecutive spaces with single space
    filteredValue = filteredValue.replace(/\s{2,}/g, ' ');

    // Reset duplicate name error when user starts typing
    if (this.isDuplicateNameSignal() && filteredValue !== this.formUserProfile().name) {
      this.isDuplicateNameSignal.set(false);
    }

    // Update the input value if it was filtered
    if (filteredValue !== value) {
      input.value = filteredValue;
    }

    // Update the model
    this.formUserProfile.update((profile) => ({
      ...profile,
      name: filteredValue,
    }));
  }

  isReservedBrandError(err: any): boolean {
    return this.forbiddenWordValidatorService.isReservedBrandError(err);
  }

  openDialogAlert(data: IDialogData) {
    this.dialogAlertSignal.set(data);
  }

  onCloseDialog() {
    this.dialogAlertSignal.update((res) => ({
      ...res,
      visible: false,
    }));
    this.loadingButtonSignal.set(false);
  }

  translateWord(key: string) {
    return this.translocoService.translate(key);
  }
}
